
import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));

    // Intercept form submit inside this quick-add component
    this.addEventListener('submit', this.#handleFormSubmit);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
    this.removeEventListener('submit', this.#handleFormSubmit);
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Intercepts the product form submit inside a quick-add card and adds
   * the variant directly to the cart without opening any modal or drawer.
   * @param {SubmitEvent} event
   */
  #handleFormSubmit = async (event) => {
    event.preventDefault();
    event.stopImmediatePropagation();

    const form = /** @type {HTMLFormElement | null} */ (event.target);
    const variantInput = /** @type {HTMLInputElement | null} */ (form?.querySelector('input[name="id"]'));
    const variantId = variantInput?.value;
    if (!variantId) return;

    const quantityInput = /** @type {HTMLInputElement | null} */ (form?.querySelector('input[name="quantity"]'));
    const quantity = Number(quantityInput?.value) || 1;

    const buttons = /** @type {NodeListOf<HTMLButtonElement>} */ (this.querySelectorAll('button[name="add"]'));
    buttons.forEach((btn) => (btn.disabled = true));

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: variantId, quantity }),
        signal: this.#abortController.signal,
      });

      if (!response.ok) throw new Error(`Cart add failed: HTTP ${response.status}`);

      const cartResponse = await fetch('/cart.js', { signal: this.#abortController.signal });
      if (!cartResponse.ok) throw new Error(`Cart fetch failed: HTTP ${cartResponse.status}`);

      const cart = await cartResponse.json();

      // CartUpdateEvent updates the cart icon/count but does NOT open the drawer
      document.dispatchEvent(new CartUpdateEvent({ cart, didError: false }));

      // Trigger the add-to-cart button's success animation
      const addToCartContainer = /** @type {any} */ (this.querySelector('add-to-cart-component'));
      addToCartContainer?.animateAddToCart?.();
    } catch (error) {
      if (/** @type {Error} */ (error).name === 'AbortError') return;
      document.dispatchEvent(new CartUpdateEvent({ didError: true }));
      throw error;
    } finally {
      this.#abortController = null;
      buttons.forEach((btn) => (btn.disabled = false));
    }
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker} */ (modalContent.querySelector('variant-picker'));
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
  event.preventDefault();

  const variantId = this.querySelector('input[name="id"]')?.value;
  if (!variantId) return;

  const button = this.querySelector('button[name="add"]');
  if (button) button.disabled = true;

  try {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.description || 'Failed to add to cart');
    }

    const cart = await fetch('/cart.js').then((res) => res.json());

    document.dispatchEvent(
      new CustomEvent(ThemeEvents.cartUpdate, {
        bubbles: true,
        detail: { data: { cart, didError: false, source: 'quick-add' } },
      })
    );
  } catch (error) {
    console.error('Quick add failed:', error);
  } finally {
    if (button) button.disabled = false;
  }
};
  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;
    const productDetails = dialogComponent.querySelector('.product-details'),
      productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);
    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), { once: true });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;
    this.#stayVisibleUntilDialogCloses(dialogComponent);
    dialogComponent.showDialog();
    const dialog = dialogComponent.refs?.dialog;
    dialog && dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    dialogComponent instanceof QuickAddDialog && dialogComponent.closeDialog();
  };

  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    try {
      const response = await fetch(productPageUrl, { signal: this.#abortController.signal });
      if (!response.ok) throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      const responseText = await response.text();
      return new DOMParser().parseFromString(responseText, 'text/html');
    } catch (error) {
      if (error.name === 'AbortError') return null;
      throw error;
    } finally {
      this.#abortController = null;
    }
  }

  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!productGrid || !modalContent) return;
    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details'),
        productFormComponent = productGrid.querySelector('product-form-component'),
        variantPicker = productGrid.querySelector('variant-picker'),
        productPrice = productGrid.querySelector('product-price'),
        productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';
      productTitle.href = this.productPageUrl;
      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');
      productHeader.appendChild(productTitle);
      productPrice && productHeader.appendChild(productPrice);
      productGrid.appendChild(productHeader);
      variantPicker && productGrid.appendChild(variantPicker);
      productFormComponent && productGrid.appendChild(productFormComponent);
      productDetails?.remove();
    }
    morph(modalContent, productGrid);
    this.#syncVariantSelection(modalContent);
  }

  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement) || event.target.closest('product-card') !== this.closest('product-card')) return;
    const quickAddButton = this.dataset.productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs)
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
  }
}
customElements.get('quick-add-component') || customElements.define('quick-add-component', QuickAddComponent);

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  handleCartUpdate = (event) => {
    event.detail.data.didError || this.closeDialog();
  };

  #updateProductTitleLink = (event) => {
    const anchorElement = event.detail.data.html?.querySelector('.view-product-title a'),
      viewMoreDetailsLink = this.querySelector('.view-product-title a'),
      mobileProductTitle = this.querySelector('.product-header a');
    if (anchorElement) {
      if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
      if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
    }
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    const skip = !iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4);
    if (skip) return;
    requestAnimationFrame(() => {
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}
customElements.get('quick-add-dialog') || customElements.define('quick-add-dialog', QuickAddDialog);